import Button from 'tio-ui/components/button';
import { dropTask } from 'ember-concurrency';
import Input from 'tio-ui/components/input';
import Switch from 'tio-ui/components/switch';
import {
  PhClock,
  PhDeviceMobile,
  PhLock,
  PhPaperPlaneTilt,
  PhSealCheck,
} from 'ember-phosphor-icons';
import { Section } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import formatPhone from 'tio-common/helpers/format-phone';
import Heading from 'tio-ui/components/heading';
import DescriptionList from 'tio-ui/components/description-list';
import inputmask from 'tio-common/modifiers/inputmask';
import PhoneInput, { DEFAULT_COUNTRY, readPhone } from 'tio-ui/components/phone-input';
import type { PhoneValue } from 'tio-ui/components/phone-input';
import MfaVerification from './mfa-verification';
import Modal from 'tio-ui/components/modal';
import PhoneVerificationFlow from '../../phone-verification/flow';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import StatusBadge from 'tio-ui/components/status-badge';
import { not } from 'tio-ui/utilities';
import {
  reloadRecord,
  saveExistingRecordWithChangelog,
  saveNewRecordWithChangelog,
  saveRecord,
} from 'tio-data/requests/save-record';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type FeaturesService from 'tio-common/services/features';
import type PhoneCaptureService from 'tio-employee/services/phone-capture';
import type { LoginExtension as LoginModel } from 'tio-data/data/resources/login.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type Owner from '@ember/owner';
import type { PhoneNumber } from 'tio-data/data/resources/phone-number.type';
import type SessionContextService from '../../../../services/session-context';
import type SessionService from '../../../../services/session';
import type { Store } from '@warp-drive/core';

export interface SecuritySettingsSignature {
  Args: {
    employee?: EmployeeModel;
    familyPerson?: PersonModel;
  };
}

/** Any already-dialable international value, whatever its region. */
function isE164(value: string): boolean {
  return /^\+[1-9]\d{9,14}$/.test(value.trim());
}

/**
 * Best-effort E.164 for a value typed or stored without one. US-only by construction —
 * PhoneInput resolves the region properly wherever it is in use, and this is the
 * fallback for the paths that still have no selector.
 */
function toUsE164(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  return value;
}

export default class SecuritySettings extends Component<SecuritySettingsSignature> {
  @service declare features: FeaturesService;
  @service declare phoneCapture: PhoneCaptureService;
  @service declare session: SessionService;
  @service declare sessionContext: SessionContextService;
  @service declare store: Store;

  @tracked showPhoneVerification = false;

  @tracked isOpen = true;
  @tracked isLoading = false;
  @tracked error = null;
  @tracked isEditingContact = false;
  @tracked phoneValue!: string;
  @tracked verificationMode?: 'mfa-enable' | 'phone-change' | 'mfa-phone-entry';
  @tracked showPhoneChangeWarning = false;
  @tracked showMfaPhoneEntry = false;
  @tracked mfaPhoneEntryValue = '';
  @tracked phoneE164: string | null = null;
  /** What the shared field holds on each surface; the string fields mirror it for the masked paths. */
  @tracked capturedPhone: PhoneValue = readPhone('', DEFAULT_COUNTRY);
  @tracked capturedMfaPhone: PhoneValue = readPhone('', DEFAULT_COUNTRY);
  /**
   * The record keeps its old `isVerified` until the server answers, and nothing may
   * read that as proof of a number the member has not verified.
   */
  @tracked private isPhoneChangeInFlight = false;
  @tracked mfaPhoneEntryE164: string | null = null;
  @tracked codeSentAt?: number;
  @tracked codeSentForMode?: 'mfa-enable' | 'mfa-phone-entry';
  @tracked codeSentForPhone?: string;

  constructor(owner: Owner, args: SecuritySettingsSignature['Args']) {
    super(owner, args);
    this.setInitialPhoneValue();
  }

  get person() {
    return this.args.familyPerson ?? this.args.employee?.person;
  }

  get primaryPhoneNumber() {
    return this.args.employee?.primaryPhoneNumber ?? this.person?.primaryPhoneNumber;
  }

  /**
   * The RFC 110 states replace the plain phone row only for an employee: a family
   * member's row has no mobile-only helper behind it, so it keeps today's markup.
   */
  get showCaptureStates() {
    return !!this.args.employee && this.features.can('phoneCapture.surfaces.profile');
  }

  get captureMobile() {
    return this.args.employee?.activeMobilePhoneNumber;
  }

  get captureState() {
    return this.args.employee?.phoneVerificationState ?? 'none';
  }

  get isCaptureVerified() {
    return this.captureState === 'verified';
  }

  get isCaptureUnverified() {
    return this.captureState === 'unverified';
  }

  /** Empty rather than undefined, so the display helper and the flow's prefill both
   * take it directly; empty is falsy in the template, which is the branch we want. */
  get captureNumber(): string {
    return this.captureMobile?.number ?? '';
  }

  get captureVerifiedAt() {
    return this.captureMobile?.verifiedAt;
  }

  get isSmsConsentOn() {
    return this.captureMobile?.smsConsent ?? false;
  }

  /**
   * The toggle stays disabled in both states, but for different reasons: unverified
   * numbers are not allowed text updates at all, and the opt-in endpoint is not
   * deployed yet.
   */
  get smsConsentHint() {
    return this.isCaptureVerified
      ? 'profile.phone_capture.text_updates_hint_pending'
      : 'profile.phone_capture.text_updates_hint_unverified';
  }

  /**
   * Prefills the flow and opens it at the number step. Entering at the code step
   * would claim "we sent a code" for a challenge nothing has started — only the
   * flow's own send button starts one.
   */
  openPhoneVerification = () => {
    this.showPhoneVerification = true;
  };

  /**
   * Refetches on any exit, not just success: `submit` persists the number before the
   * code step, so abandoning it still changed what the row should show.
   */
  closePhoneVerification = () => {
    this.showPhoneVerification = false;
    this.refreshPhoneNumbers.perform();
  };

  onPhoneVerified = () => {
    this.showPhoneVerification = false;
    this.refreshPhoneNumbers.perform();
  };

  refreshPhoneNumbers = dropTask(async () => {
    if (!this.args.employee) {
      return;
    }

    await reloadRecord(this.store, this.args.employee, { include: 'phone-numbers' });
    this.setInitialPhoneValue();
  });

  setInitialPhoneValue() {
    this.phoneValue = toUsE164(this.primaryPhoneNumber?.number || '');
    this.capturedPhone = readPhone(this.phoneValue, DEFAULT_COUNTRY);
    this.phoneE164 = this.capturedPhone.e164;
  }

  get login() {
    return this.person?.user?.logins?.find((login) => login.isActive);
  }

  get userName() {
    return this.login ? this.login.userName : '';
  }

  get isMfaEnabled() {
    return this.login?.twoFactorEnabled ?? false;
  }

  get mfaPhoneNumber() {
    return this.primaryPhoneNumber?.number || this.phoneValue || '';
  }

  get isMfaToggleDisabled() {
    return this.updateMfaSettings.isRunning || this.isPhoneChangeInFlight;
  }

  /** Reads the saved row, not the edit buffer: MFA still texts the old number mid-retype. */
  get showMfaPhonePrompt() {
    return !this.primaryPhoneNumber?.number;
  }

  get showMfaPhoneNumber() {
    return !this.showMfaPhonePrompt && !!this.mfaPhoneNumber;
  }

  toggleExpand = () => {
    this.isOpen = !this.isOpen;
  };

  toggleContactEdit = () => {
    this.isEditingContact = !this.isEditingContact;
    this.phoneE164 = null;
    this.setInitialPhoneValue();
  };

  setPhoneValue = (value: string) => {
    // Store raw value during editing; normalization happens on blur
    this.phoneValue = value || '';
    // The masked field never reports a resolved number.
    this.phoneE164 = null;
  };

  setCapturedPhone = (phone: PhoneValue) => {
    this.capturedPhone = phone;
    this.phoneValue = phone.national;
    this.phoneE164 = phone.e164;
  };

  onPhoneBlur = () => {
    this.normalizePhoneValue();
  };

  private normalizePhoneValue() {
    this.phoneValue = toUsE164(this.phoneValue);
  }

  get isPhoneNumberValid() {
    // Only the shared field yields a number outside the NANP, so an E.164 shape counts
    // only where it is driving — the masked field's verdict must not move.
    if (this.showCaptureStates && (this.phoneE164 || isE164(this.phoneValue))) {
      return true;
    }

    const digits = this.phoneValue.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  }

  get isSubmitDisabled() {
    // While the shared field is driving, its verdict is the only one that counts: the
    // digit-count fallback would accept a NANP shape PhoneInput itself rejects.
    if (this.showCaptureStates && this.isEditingContact && !this.phoneE164) {
      return true;
    }

    if (!this.isPhoneNumberValid) {
      return true;
    }

    return !this.isPhoneNumberEdited;
  }

  /**
   * Canonical comparison stops an unchanged legacy national-format number reading as an
   * edit, but that changes what Save and the MFA warning do, so it waits for the flag.
   */
  get isPhoneNumberEdited() {
    return this.showCaptureStates
      ? this.normalizedPhoneValue !== this.storedNumberCanonical
      : this.phoneValue !== this.primaryPhoneNumber?.number;
  }

  get normalizedPhoneValue() {
    // PhoneInput resolves the region properly; the fallback below assumes +1, which is
    // wrong for the MX and CA numbers the selector offers.
    return this.phoneE164 ?? toUsE164(this.phoneValue);
  }

  /**
   * The stored number in the same shape as the field's, so an unchanged legacy
   * national-format record does not read as an edit.
   */
  get storedNumberCanonical() {
    return toUsE164(this.primaryPhoneNumber?.number ?? '');
  }

  savePhoneNumber = dropTask(async () => {
    this.normalizePhoneValue();

    if (this.isMfaEnabled && this.isPhoneNumberEdited) {
      this.showPhoneChangeWarning = true;
      return;
    }

    await this.doSavePhone();
  });

  private async doSavePhone() {
    this.normalizePhoneValue();
    this.clearCodeSentState();
    const normalizedPhone = this.normalizedPhoneValue;

    this.isPhoneChangeInFlight = true;
    try {
      // Only a mobile row is written over; a live landline would lose its number and
      // keep its type, invisible to every mobile reader.
      if (this.primaryPhoneNumber?.phoneNumberType === 'MOBILE') {
        this.primaryPhoneNumber.number = normalizedPhone;
        // WarpDrive save helper (Phase 2.7) — replaces
        // record.save({ adapterOptions: { changelog } }) on an EXISTING record.
        await saveExistingRecordWithChangelog(
          this.store,
          this.primaryPhoneNumber,
          'updated by employee'
        );
      } else if (this.person?.user) {
        const newPhoneNumber = this.store.createRecord<PhoneNumber>('phone-number', {
          contactType: 'TELEPHONE',
          number: normalizedPhone,
          phoneNumberType: 'MOBILE',
          user: this.person.user,
        });
        // WarpDrive save helper (Phase 2.7) — replaces
        // record.save({ adapterOptions: { changelog } }) on a NEW record. The
        // `_changelog` is a write-only PaperTrail attribute (not a model
        // attribute), so it is injected into the serialized POST body by
        // saveNewRecordWithChangelog rather than dropped by plain saveNewRecord.
        await saveNewRecordWithChangelog(this.store, newPhoneNumber, 'added by employee');
      }

      if (this.args.employee) {
        // Standalone refresh (NOT after a builder mutation on this record) to pick
        // up the just-saved phone relationship — keep the refetch via the helper.
        await reloadRecord(this.store, this.args.employee, { include: 'phone-numbers' });
      } else if (this.args.familyPerson) {
        await reloadRecord(this.store, this.args.familyPerson, { include: 'phone-numbers' });
      }
    } finally {
      // A failed save leaves the record holding the new number, so the lock has to
      // lift on the error path too or MFA stays unreachable.
      this.isPhoneChangeInFlight = false;
    }

    this.toggleContactEdit();
  }

  confirmPhoneChange = () => {
    this.showPhoneChangeWarning = false;
    this.verificationMode = 'phone-change';
  };

  cancelPhoneChange = () => {
    this.showPhoneChangeWarning = false;
  };

  get isMfaPhoneEntryValid() {
    // While the shared field is driving, its verdict is the only one: the digit count
    // would let a NANP shape it rejects through the +1 fallback.
    if (this.showCaptureStates) {
      return this.mfaPhoneEntryE164 !== null;
    }

    const digits = this.mfaPhoneEntryValue.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  }

  get normalizedMfaPhoneEntryValue() {
    return this.mfaPhoneEntryE164 ?? toUsE164(this.mfaPhoneEntryValue);
  }

  setMfaPhoneEntryValue = (value: string) => {
    this.mfaPhoneEntryValue = value || '';
    this.mfaPhoneEntryE164 = null;
  };

  setCapturedMfaPhone = (phone: PhoneValue) => {
    this.capturedMfaPhone = phone;
    this.mfaPhoneEntryValue = phone.national;
    this.mfaPhoneEntryE164 = phone.e164;
  };

  private resetMfaPhoneEntry() {
    this.mfaPhoneEntryValue = '';
    this.mfaPhoneEntryE164 = null;
    this.capturedMfaPhone = readPhone('', DEFAULT_COUNTRY);
  }

  onMfaPhoneEntrySubmit = () => {
    if (!this.isMfaPhoneEntryValid) return;
    this.showMfaPhoneEntry = false;
    this.verificationMode = 'mfa-phone-entry';
  };

  onMfaPhoneEntryCancel = () => {
    this.showMfaPhoneEntry = false;
    this.resetMfaPhoneEntry();
  };

  updateMfaSettings = dropTask(async (isSelected: boolean) => {
    if (!this.login) {
      return;
    }

    if (isSelected) {
      // If a verification code was already sent for enabling MFA, reopen verification directly
      if (this.hasActiveCode && this.codeSentForMode === 'mfa-enable') {
        this.verificationMode = 'mfa-enable';
        return;
      }

      if (!this.isPhoneNumberValid) {
        // Preserve previously entered phone if a code was sent for it
        if (!(this.hasActiveCode && this.codeSentForMode === 'mfa-phone-entry')) {
          this.resetMfaPhoneEntry();
        }
        this.showMfaPhoneEntry = true;
        return;
      }

      const phone = this.primaryPhoneNumber;
      if (phone?.isVerified && !this.isPhoneChangeInFlight) {
        this.login.twoFactorEnabled = true;
        await saveRecord(this.store, this.login as LoginModel);
      } else {
        this.verificationMode = 'mfa-enable';
      }
    } else {
      this.login.twoFactorEnabled = false;
      await saveRecord(this.store, this.login as LoginModel);
    }
  });

  get showVerification() {
    return !!this.verificationMode;
  }

  get verificationPhoneNumber() {
    if (this.verificationMode === 'mfa-phone-entry') {
      return this.normalizedMfaPhoneEntryValue;
    }
    return this.mfaPhoneNumber;
  }

  get verificationNewPhoneNumber() {
    if (this.verificationMode === 'phone-change') {
      return this.normalizedPhoneValue;
    }
    if (this.verificationMode === 'mfa-phone-entry') {
      return this.normalizedMfaPhoneEntryValue;
    }
    return undefined;
  }

  get verificationAutoSend() {
    if (this.verificationInitialStep === 'enter-code') return false;
    return this.verificationMode === 'phone-change' || this.verificationMode === 'mfa-phone-entry';
  }

  get hasActiveCode() {
    if (!this.codeSentAt || !this.codeSentForMode) return false;
    const TEN_MINUTES = 10 * 60 * 1000;
    return Date.now() - this.codeSentAt < TEN_MINUTES;
  }

  get verificationInitialStep() {
    if (!this.hasActiveCode || !this.verificationMode) return undefined;
    if (this.verificationMode !== this.codeSentForMode) return undefined;
    if (this.verificationPhoneNumber !== this.codeSentForPhone) return undefined;
    return 'enter-code' as const;
  }

  private clearCodeSentState() {
    this.codeSentAt = undefined;
    this.codeSentForMode = undefined;
    this.codeSentForPhone = undefined;
  }

  onCodeSent = () => {
    if (this.verificationMode !== 'phone-change') {
      this.codeSentAt = Date.now();
      this.codeSentForMode = this.verificationMode as 'mfa-enable' | 'mfa-phone-entry';
      this.codeSentForPhone = this.verificationPhoneNumber;
    }
  };

  onVerificationComplete = async () => {
    const mode = this.verificationMode;
    this.verificationMode = undefined;
    this.clearCodeSentState();

    if (mode === 'mfa-enable' || mode === 'mfa-phone-entry') {
      if (this.login) {
        this.login.twoFactorEnabled = true;
        await saveRecord(this.store, this.login as LoginModel);
      }
    }

    // Reload to get updated phone records (standalone refresh, NOT after a
    // builder mutation on this record) — keep the refetch via the helper.
    if (this.args.employee) {
      await reloadRecord(this.store, this.args.employee, { include: 'phone-numbers' });
    } else if (this.args.familyPerson) {
      await reloadRecord(this.store, this.args.familyPerson, { include: 'phone-numbers' });
    }

    if (mode === 'phone-change' || mode === 'mfa-phone-entry') {
      this.setInitialPhoneValue();
    }
    if (mode === 'phone-change') {
      this.isEditingContact = false;
    }
    if (mode === 'mfa-phone-entry') {
      this.resetMfaPhoneEntry();
    }
  };

  onVerificationCancel = () => {
    const mode = this.verificationMode;
    this.verificationMode = undefined;

    // For phone-change, always clear codeSent state (security-sensitive)
    if (mode === 'phone-change') {
      this.clearCodeSentState();
    }

    // Only clear phone entry value if no active code was sent for this flow
    if (mode === 'mfa-phone-entry') {
      const codeSentForThisFlow = this.hasActiveCode && this.codeSentForMode === 'mfa-phone-entry';
      if (!codeSentForThisFlow) {
        this.resetMfaPhoneEntry();
      }
    }
  };

  <template>
    <Section>
      <:header>{{t "profile.security_settings"}}</:header>
      <:body>
        <DescriptionList @layout="settings" as |List|>
          <List.Group>
            <List.Term>{{t "profile.personal_info"}}</List.Term>
            <List.Details>
              <DescriptionList @layout="summary" class="grid-cols-2" as |Details|>
                <Details.Group>
                  <Details.Term>{{t "login.first_name"}}</Details.Term>
                  <Details.Details>{{this.person.firstName}}</Details.Details>
                </Details.Group>
                <Details.Group>
                  <Details.Term>{{t "login.last_name"}}</Details.Term>
                  <Details.Details>{{this.person.lastName}}</Details.Details>
                </Details.Group>
              </DescriptionList>
            </List.Details>
            <List.Actions class="tio-fg-muted tio-col items-center text-center">
              <PhLock @size="24" @weight="fill" />
              <span class="tio-text-xs-normal">{{t "profile.to_edit_contact_support"}}</span>
            </List.Actions>
          </List.Group>
          <List.Group>
            <List.Term>{{t "profile.username_login"}}</List.Term>
            <List.Details>
              <DescriptionList @layout="summary" class="grid-cols-1" as |Details|>
                <Details.Group>
                  <Details.Term>{{t "profile.username"}}</Details.Term>
                  <Details.Details>{{this.userName}}</Details.Details>
                </Details.Group>
              </DescriptionList>
            </List.Details>
            <List.Actions class="tio-fg-muted tio-col items-center text-center">
              <PhLock @size="24" @weight="fill" />
              <span class="tio-text-xs-normal">{{t "profile.to_edit_contact_support"}}</span>
            </List.Actions>
          </List.Group>
          <List.Group>
            <List.Term>{{t "profile.contact_info"}}</List.Term>
            <List.Details>
              {{#if this.isEditingContact}}
                {{#if this.showCaptureStates}}
                  <PhoneInput
                    @testHook="profile-phone"
                    @value={{this.capturedPhone}}
                    @onChange={{this.setCapturedPhone}}
                    @countries={{this.phoneCapture.regions}}
                    @label={{t "profile.mobile_phone"}}
                  />
                {{else}}
                  <Input
                    @value={{this.phoneValue}}
                    @type="tel"
                    @onChange={{this.setPhoneValue}}
                    @onBlur={{this.onPhoneBlur}}
                    @label={{t "profile.mobile_phone"}}
                    placeholder="+1 (###) ###-####"
                    {{inputmask mask="+1 (999) 999-9999"}}
                    data-test-phone-input
                  />
                {{/if}}
              {{else if this.showCaptureStates}}
                {{#if this.captureNumber}}
                  <div class="tio-wrap-tight">
                    <span class="tio-text-xl-normal">{{formatPhone this.captureNumber}}</span>
                    {{#if this.isCaptureVerified}}
                      <StatusBadge
                        @intent="success"
                        @icon={{PhSealCheck}}
                        @label={{t "profile.phone_capture.verified"}}
                        data-test-profile-phone-verified-badge
                      />
                    {{else if this.isCaptureUnverified}}
                      <StatusBadge
                        @intent="warning"
                        @icon={{PhClock}}
                        @label={{t "profile.phone_capture.unverified"}}
                        data-test-profile-phone-unverified-badge
                      />
                    {{/if}}
                  </div>

                  {{#if this.isCaptureVerified}}
                    <span
                      class="tio-text-xs-normal tio-fg-muted"
                      data-test-profile-phone-verified-on
                    >
                      {{t
                        "profile.phone_capture.verified_on"
                        date=(safeFormatDate
                          this.captureVerifiedAt month="short" day="numeric" year="numeric"
                        )
                      }}
                    </span>
                  {{else if this.isCaptureUnverified}}
                    <Button
                      @intent="primary"
                      @size="sm"
                      class="self-start"
                      {{on "click" this.openPhoneVerification}}
                      data-test-profile-phone-verify-now
                    >
                      <PhPaperPlaneTilt @size={{16}} />
                      {{t "profile.phone_capture.verify_now"}}
                    </Button>
                    <span
                      class="tio-text-xs-normal tio-fg-muted"
                      data-test-profile-phone-verify-hint
                    >
                      {{t "profile.phone_capture.verify_hint"}}
                    </span>
                  {{/if}}
                {{else}}
                  {{! A member with only a landline still has a number on file; the
                      capture branch used to hide it while inviting them to add one. }}
                  {{#if this.phoneValue}}
                    <span class="tio-text-xl-normal" data-test-profile-phone-non-mobile>
                      {{formatPhone this.phoneValue}}
                    </span>
                  {{/if}}
                  <Button
                    @appearance="outlined"
                    @intent="primary"
                    class="self-start"
                    {{on "click" this.openPhoneVerification}}
                    data-test-profile-phone-add
                  >
                    {{t "profile.phone_capture.add_number"}}
                  </Button>
                {{/if}}
              {{else if this.phoneValue}}
                <span class="tio-text-xl-normal">
                  {{formatPhone this.phoneValue}}
                </span>
              {{else}}
                <span class="tio-text-xs-normal tio-fg-danger">
                  {{t "profile.no_phone"}}
                </span>
              {{/if}}
            </List.Details>
            <List.Actions>
              {{#if this.isEditingContact}}
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  {{on "click" this.savePhoneNumber.perform}}
                  @isDisabled={{this.isSubmitDisabled}}
                  data-test-contact-save
                >
                  {{t "common.save"}}
                </Button>
                <Button
                  @appearance="outlined"
                  @intent="danger"
                  {{on "click" this.toggleContactEdit}}
                  data-test-contact-cancel
                >
                  {{t "common.cancel"}}
                </Button>
              {{else}}
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  {{on "click" this.toggleContactEdit}}
                  class="self-auto"
                  data-test-contact-edit
                >
                  {{t "common.edit"}}
                </Button>
              {{/if}}
            </List.Actions>
          </List.Group>
          {{#if this.showCaptureStates}}
            <List.Group data-test-profile-text-updates>
              <List.Term>{{t "profile.phone_capture.text_updates"}}</List.Term>
              <List.Details>
                <span class="tio-text-xs-normal">{{t
                    "profile.phone_capture.text_updates_description"
                  }}</span>
              </List.Details>
              <List.Actions>
                <Switch
                  @checked={{this.isSmsConsentOn}}
                  @isDisabled={{true}}
                  @label={{t "profile.phone_capture.text_updates_label"}}
                  @hint={{t this.smsConsentHint}}
                />
              </List.Actions>
            </List.Group>
          {{/if}}
          <List.Group data-test-mfa-section>
            <List.Term>{{t "profile.mfa.title"}}</List.Term>
            <List.Details>
              <span class="tio-text-xs-normal">{{t "profile.mfa.status"}}</span>
              <span class="tio-text-xl-normal" data-test-mfa-status>
                {{if
                  this.isMfaEnabled
                  (t "profile.mfa.status_enabled")
                  (t "profile.mfa.status_disabled")
                }}
              </span>
              {{#if this.showMfaPhonePrompt}}
                <span class="tio-text-xs-normal tio-fg-danger" data-test-mfa-phone-required>
                  {{t "profile.mfa.phone_required"}}
                </span>
              {{else if this.showMfaPhoneNumber}}
                <span class="tio-text-xs-normal tio-fg-muted" data-test-mfa-phone-number>
                  {{t "profile.mfa.sms_number"}}
                  {{formatPhone this.mfaPhoneNumber}}
                </span>
              {{/if}}
            </List.Details>
            <List.Actions>
              <div data-test-mfa-toggle>
                <Switch
                  @checked={{this.isMfaEnabled}}
                  @onChange={{this.updateMfaSettings.perform}}
                  @isDisabled={{this.isMfaToggleDisabled}}
                  @label={{t "profile.mfa.toggle_label"}}
                />
              </div>
            </List.Actions>
          </List.Group>
        </DescriptionList>
      </:body>
    </Section>

    {{#if this.showCaptureStates}}
      <Modal
        @isOpen={{this.showPhoneVerification}}
        @onClose={{this.closePhoneVerification}}
        @size="md"
        data-test-profile-phone-verification
      >
        <:body>
          <PhoneVerificationFlow
            @surface="profile"
            @phoneNumber={{this.captureNumber}}
            @onVerified={{this.onPhoneVerified}}
          />
        </:body>
      </Modal>
    {{/if}}

    {{! MFA / Phone Verification Modal (consolidated) }}
    <MfaVerification
      @show={{this.showVerification}}
      @phoneNumber={{this.verificationPhoneNumber}}
      @newPhoneNumber={{this.verificationNewPhoneNumber}}
      @autoSend={{this.verificationAutoSend}}
      @initialStep={{this.verificationInitialStep}}
      @onVerified={{this.onVerificationComplete}}
      @onCancel={{this.onVerificationCancel}}
      @onCodeSent={{this.onCodeSent}}
    />

    {{! Phone Change Warning }}
    <Modal
      @isOpen={{this.showPhoneChangeWarning}}
      @onClose={{this.cancelPhoneChange}}
      @size="sm"
      data-test-phone-change-warning
    >
      <:header>
        <Heading @level={{3}}>
          {{t "profile.mfa.phone_change_warning.title"}}
        </Heading>
      </:header>
      <:body>
        <p class="text-sm tio-fg-secondary">
          {{t "profile.mfa.phone_change_warning.message"}}
        </p>
      </:body>
      <:footer>
        <div class="tio-flex-end gap-3">
          <Button
            @appearance="outlined"
            @intent="default"
            {{on "click" this.cancelPhoneChange}}
            data-test-phone-change-cancel
          >
            {{t "profile.mfa.phone_change_warning.cancel"}}
          </Button>
          <Button
            @intent="primary"
            {{on "click" this.confirmPhoneChange}}
            data-test-phone-change-continue
          >
            {{t "profile.mfa.phone_change_warning.continue"}}
          </Button>
        </div>
      </:footer>
    </Modal>

    {{! MFA Phone Entry Modal (no phone on file) }}
    <Modal
      @isOpen={{this.showMfaPhoneEntry}}
      @onClose={{this.onMfaPhoneEntryCancel}}
      @size="md"
      data-test-mfa-phone-entry
    >
      <:header>
        <div class="tio-row">
          <div class="w-10 h-10 rounded-xl tio-bg-brand-soft tio-row-center">
            <PhDeviceMobile class="tio-fg-brand" @size={{20}} />
          </div>
          <Heading @level={{2}} @size="lg">
            {{t "profile.mfa.phone_entry.title"}}
          </Heading>
        </div>
      </:header>
      <:body>
        <div class="space-y-4">
          <p class="text-sm tio-fg-secondary">
            {{t "profile.mfa.phone_entry.description"}}
          </p>
          {{#if this.showCaptureStates}}
            <PhoneInput
              @testHook="mfa-phone-entry"
              @value={{this.capturedMfaPhone}}
              @onChange={{this.setCapturedMfaPhone}}
              @countries={{this.phoneCapture.regions}}
              @label={{t "profile.mobile_phone"}}
            />
          {{else}}
            <Input
              @value={{this.mfaPhoneEntryValue}}
              @type="tel"
              @onChange={{this.setMfaPhoneEntryValue}}
              @label={{t "profile.mobile_phone"}}
              placeholder="+1 (###) ###-####"
              {{inputmask mask="+1 (999) 999-9999"}}
              data-test-mfa-phone-entry-input
            />
          {{/if}}
        </div>
      </:body>
      <:footer>
        <div class="tio-flex-end gap-3">
          <Button
            @appearance="outlined"
            @intent="default"
            {{on "click" this.onMfaPhoneEntryCancel}}
            data-test-mfa-phone-entry-cancel
          >
            {{t "common.cancel"}}
          </Button>
          <Button
            @intent="primary"
            @isDisabled={{not this.isMfaPhoneEntryValid}}
            {{on "click" this.onMfaPhoneEntrySubmit}}
            data-test-mfa-phone-entry-continue
          >
            {{t "profile.mfa.phone_entry.continue"}}
          </Button>
        </div>
      </:footer>
    </Modal>
  </template>
}
